// The Vue build version to load with the `import` command
// (runtime-only or standalone) has been set in webpack.base.conf with an alias.
import Vue from 'vue'
import router from '@/router'
import store from '@/store'
import App from './App'
import ApiService from '@/common/api.service'
import toastr from 'toastr/toastr'
import TextInput from '@/components/inputs/TextInput'
import TextAreaInput from '@/components/inputs/TextAreaInput'
import SignalButton from '@/components/inputs/SignalButton'
import CopyToClipboard from '@/components/utils/CopyToClipboard'
import moment from 'moment'
import dt from '@/common/dateUtils'
import security from '@/common/security'
import 'select2/dist/js/select2.min'
import 'gijgo/css/gijgo.min.css'
import { Keys } from 'keycuts'

import * as pubnub from '@/common/pubnub'
import {
  API_SET_BUSY_INTERVAL_ID,
  API_PURGE_RESULTS,
  API_SET_POSTED,
  API_SET_BUSY_INDICATOR,
  API_SET_BUSY_INTERVAL_TS,
  API_SET_REQUEST_BUSY
} from '@/store/api/postStatus.types'

import {
  AUTH_SET_IS_INVITE,
  AUTH_IS_AUTHENTICATED,
  AUTH_CURRENT_USER
} from '@/store/auth/auth.types'

import {
  NAV_GET_MENU_OPEN,
  NAV_SET_MENU_OPEN,
  NAV_GET_SEARCH_QUERY,
  NAV_GET_FILTERED_MENU_ITEMS
} from '@/store/navigation/nav.types'

// Font-Awesome 5 Free
import { library, dom } from '@fortawesome/fontawesome-svg-core'
import {
  faBars,
  faHome,
  faPowerOff,
  faUmbrella,
  faAngleRight,
  faAngleLeft,
  faAngleUp,
  faAngleDown,
  faAngleDoubleRight,
  faAngleDoubleLeft,
  faDesktop,
  faCircle as fasCircle,
  faCubes,
  faSquare,
  faMapMarker,
  faUserCircle,
  faUnlockAlt,
  faIdCard,
  faCar,
  faPlusCircle,
  faSearch,
  faSitemap,
  faSuitcase,
  faHandshake,
  faUsers,
  faCheck,
  faExclamationTriangle,
  faPoo,
  faInfoCircle,
  faStickyNote,
  faPaperclip,
  faMagic,
  faIdCardAlt,
  faTimes,
  faDownload,
  faEdit,
  faTablet,
  faMicrochip,
  faExchangeAlt,
  faCamera,
  faHdd,
  faBug,
  faStopCircle,
  faCircleNotch,
  faSpinner,
  faCrosshairs,
  faMobile,
  faSync,
  faEye,
  faRoad,
  faImage,
  faImages,
  faTree,
  faCog,
  faMinusCircle,
  faTools,
  faSearchLocation,
  faMapMarkerAlt,
  faDirections,
  faClock,
  faCalendar,
  faArrowUp,
  faArrowDown,
  faArrowRight,
  faChevronLeft,
  faChevronRight,
  faChevronDown,
  faCalendarCheck,
  faTrashAlt,
  faTimesCircle,
  faRuler,
  faUserCheck,
  faGlobe,
  faRoute,
  faHistory,
  faFastBackward,
  faFastForward,
  faBackward,
  faForward,
  faPlay,
  faPause,
  faStop,
  faBatteryQuarter,
  faCarCrash,
  faExclamation,
  faInfo,
  faTemperatureLow,
  faTemperatureHigh,
  faToolbox,
  faVolumeUp,
  faVolumeMute,
  faTrash,
  faFile,
  faUser,
  faCheckCircle,
  faCity,
  faSun,
  faCopy,
  faHourglassStart,
  faLink,
  faSimCard,
  faCheckSquare
} from '@fortawesome/free-solid-svg-icons'

// import {
//   faCircle as farCircle
// } from '@fortawesome/free-regular-svg-icons'

// Font-Awesome 5 Pro
import {
  faRectangleWide,
  faFileChartLine,
  faTachometerAltFastest,
  faBellExclamation,
  faBatterySlash,
  faCarTilt,
  faEngineWarning,
  faBoot,
  faLocationSlash,
  faAlarmExclamation,
  faSensorOn,
  faPortalEnter,
  faDrawPolygon,
  faMoneyCheckEditAlt,
  faRepeat,
  faUserHeadset,
  faGarageCar,
  faChevronDoubleRight
} from '@fortawesome/pro-solid-svg-icons'

// Font-Awesome 5 Pro Due-tone
import {
  faGlobeAfrica as fadGlobeAfrica
} from '@fortawesome/pro-duotone-svg-icons'

// Font-Awesome 5 Pro Light
import {
  faCars as falCars,
  faRoad as falRoad,
  faDrawCircle as falDrawCircle,
  faDrawSquare as falDrawSquare,
  faDrawPolygon as falDrawPolygon,
  faCopy as falCopy,
  faSms as falSms
} from '@fortawesome/pro-light-svg-icons'

import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'

library.add(faBars) // Menu
library.add(faHome)
library.add(faPowerOff) // Logout
library.add(faUmbrella) // Partners
library.add(faAngleRight)
library.add(faAngleLeft)
library.add(faAngleUp)
library.add(faAngleDown)
library.add(faAngleDoubleRight)
library.add(faAngleDoubleLeft)
library.add(faDesktop)
library.add(fasCircle) // use <fa-icon :icon="['fas', 'circle']"/>
library.add(faCubes)
library.add(faSquare)
library.add(faMapMarker) // Monitoring
library.add(faUserCircle) // My Account
library.add(faUnlockAlt) // Administration
library.add(faIdCard) // Drivers
library.add(faCar) // Vehicles
library.add(falCars) // use <fa-icon :icon="['fal', 'cars']"/>
library.add(faPlusCircle) // Create
library.add(faSearch) // Find
library.add(faSitemap) // Account Type
library.add(faSuitcase) // Agencies
library.add(faHandshake) // Clients
library.add(faUsers)
library.add(faCheck)
library.add(faExclamationTriangle)
library.add(faPoo)
library.add(faInfoCircle)
library.add(faStickyNote)
library.add(faPaperclip)
library.add(faMagic)
library.add(faIdCardAlt) // Licence
library.add(faTimes)
library.add(faDownload)
library.add(faEdit)
library.add(faTablet)
library.add(faMicrochip)
library.add(faExchangeAlt)
library.add(faCamera)
library.add(faHdd) // Units
library.add(faBug)
library.add(faStopCircle)
library.add(faCircleNotch)
library.add(faSpinner)
library.add(faCrosshairs)
library.add(faMobile)
library.add(faSync)
library.add(faEye)
library.add(faRoad)
library.add(falRoad) // use <fa-icon :icon="['fal', 'road']"/>
library.add(faImage)
library.add(faImages)
library.add(faTree)
library.add(faCog)
library.add(faMinusCircle)
library.add(faTrash)
library.add(faTools)
library.add(faSearchLocation)
library.add(faMapMarkerAlt)
library.add(faDirections)
library.add(faClock)
library.add(faCalendar)
library.add(faArrowUp)
library.add(faArrowDown)
library.add(faArrowRight)
library.add(faChevronLeft)
library.add(faChevronRight)
library.add(faChevronDown)
library.add(faCalendarCheck)
library.add(faTrashAlt)
library.add(faTimesCircle)
library.add(faRectangleWide)
library.add(faRuler)
library.add(fadGlobeAfrica)
library.add(faUserCheck)
library.add(faGlobe)
library.add(faFileChartLine)
library.add(faRoute)
library.add(faHistory)
library.add(faFastBackward)
library.add(faFastForward)
library.add(faBackward)
library.add(faForward)
library.add(faPlay)
library.add(faPause)
library.add(faStop)
library.add(faTachometerAltFastest)
library.add(faBellExclamation)
library.add(faBatteryQuarter)
library.add(faBatterySlash)
library.add(faCarCrash)
library.add(faCarTilt)
library.add(faEngineWarning)
library.add(faExclamation)
library.add(faInfo)
library.add(faBoot)
library.add(faTemperatureLow)
library.add(faTemperatureHigh)
library.add(faLocationSlash)
library.add(faToolbox)
library.add(faGlobe)
library.add(faAlarmExclamation)
library.add(faSensorOn)
library.add(faPortalEnter)
library.add(faVolumeUp)
library.add(faVolumeMute)
library.add(faDrawPolygon)
library.add(falDrawPolygon)
library.add(falDrawCircle)
library.add(falDrawSquare)
library.add(faMoneyCheckEditAlt)
library.add(faFile)
library.add(faRepeat)
library.add(faUserHeadset)
library.add(faUser)
library.add(faCheckCircle)
library.add(faGarageCar)
library.add(faCity)
library.add(faSun)
library.add(faCopy)
library.add(faHourglassStart)
library.add(faLink)
library.add(falCopy)
library.add(faSimCard)
library.add(falSms)
library.add(faCheckSquare)
library.add(faChevronDoubleRight)

Vue.component('fa-icon', FontAwesomeIcon)

require('bootstrap')
require('gijgo')

require('vue-bootstrap-datetimepicker')

jQuery.extend(true, jQuery.fn.datetimepicker.defaults, {
  icons: {
    time: 'fas fa-clock',
    date: 'fas fa-calendar',
    up: 'fas fa-arrow-up',
    down: 'fas fa-arrow-down',
    previous: 'fas fa-chevron-left',
    next: 'fas fa-chevron-right',
    today: 'fas fa-calendar-check',
    clear: 'fas fa-trash-alt',
    close: 'fas fa-times-circle'
  }
})
Vue.config.productionTip = false

toastr.options = {
  'positionClass': 'toast-bottom-right',
  'preventDuplicates': true,
  'progressBar': true
}

// Enable in case we ever want to use Font Awesome 5 within css.
// WARNING: fontawesome-all.min.js will also have to be included
// but it is huge (680kb+), so be warned.
// window.FontAwesomeConfig.searchPseudoElements = true

ApiService.init()
store.commit(API_PURGE_RESULTS)
store.commit(AUTH_SET_IS_INVITE, false)

// Ensure we checked auth before each page load.
router.beforeEach(
  (to, from, next) => {
    // Check to see if route is from an email invite
    // and setting AUTH_SET_IS_INVITE to true that
    // is used in App.vue te render a "Guest" component
    // that does not have a navigation menu.
    if (to.name === 'user-register-complete') {
      store.commit(AUTH_SET_IS_INVITE, true)
    }
    /*
    return Promise
      .all([store.dispatch(AUTH_CHECK_AUTH)])
      .then(next)
    */

    // Make sure our progress indicator is not running
    store.commit(API_SET_BUSY_INDICATOR, false)
    store.commit(API_SET_BUSY_INTERVAL_TS, null)
    store.commit(API_SET_BUSY_INTERVAL_ID, null)
    store.commit(API_SET_REQUEST_BUSY, false)

    // Reset the form state so that form inputs does not think
    // it needs to show success or error borders.
    store.commit(API_SET_POSTED, false)
    return next()
  }

)

// Global components
Vue.component('ws-text-input', TextInput)
Vue.component('ws-text-area-input', TextAreaInput)
Vue.component('ws-signal-button', SignalButton)
Vue.component('ws-copy-to-clipboard', CopyToClipboard)

// Global Vue scope
Vue.prototype.moment = moment
Vue.prototype.dt = dt
Vue.prototype.security = security

// Hide the application loader
document.getElementsByTagName('body')[0].className += ' app-loaded'

/* eslint-disable no-new */
new Vue({
  el: '#app',
  router,
  store,
  components: { App },
  template: '<App/>'
})

const keys = new Keys()
keys.bind(['Control', 'm'], () => {
  // Open/close the menu if the user is logged in
  if (store.getters[AUTH_IS_AUTHENTICATED]) {
    if (!store.getters[NAV_GET_MENU_OPEN]) {
      setTimeout(() => {
        document.getElementById('nav_quick_find').focus()
      }, 100)
    }
    store.commit(NAV_SET_MENU_OPEN, !store.getters[NAV_GET_MENU_OPEN])
  }
})

keys.bind(['Enter'], () => {
  // If the user is logged in, the menu is open and a search
  // query is active, then route to the first available
  // filtered menu item when the user presses Enter.
  if (store.getters[AUTH_IS_AUTHENTICATED]) {
    if (store.getters[NAV_GET_MENU_OPEN] && store.getters[NAV_GET_SEARCH_QUERY]) {
      const filteredMenuItems = store.getters[NAV_GET_FILTERED_MENU_ITEMS]
      if (filteredMenuItems.length > 0) {
        store.commit(NAV_SET_MENU_OPEN, false)
        router.push(filteredMenuItems[0].routerTo)
      }
    }
  }
})

const user = store.getters[AUTH_CURRENT_USER]
if (user.id !== null) {
  pubnub.init(user.pubsub)
}

// This was added because the tempusdominus-bootstrap-4 date/time picker
// still uses <i> tags to render the icons. Unless the plugin is updated
// to use the new Font Awesome 5 format we have to automatically replace
// <i> tags that are rendered. See:
// https://github.com/FortAwesome/vue-fontawesome#processing-i-tags-into-svg-using-font-awesome
dom.watch()



// WEBPACK FOOTER //
// ./src/main.js